import { isSameDay, parse } from "date-fns";
import React, { useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

import { ButtonStandard } from "@performyard/py-components";
import { Text } from "@performyard/py-components";
import { InputWrapper, SingleSelectInput } from "@performyard/py-components";

import DatePicker from "../../../../../components/Inputs/DatePicker";
import Input from "../../../../../components/Inputs/InputText";
import InputTextArea from "../../../../../components/Inputs/InputTextArea";
import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
import ContentModal from "../../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../../components/Spacer";

import { useLatestMeetingCarryOverItems } from "../../../../../api/hooks";
import {
	useCompanyCalendarIntegrationSettings,
	useUserCalendarIntegrationStatus,
} from "../../../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

import { generateTimeOptions, handleEndDateChange, handleStartDateChange, handleStartTimeChange } from "../../utils";

import { config } from "../../config";
import { FormErrors, MeetingFormSchema } from "../../schema";

export interface MeetingFormData {
	name: string;
	description: string;
	startDateTime: Date;
	endDateTime: Date;
	carryOver: boolean;
	addToCalendar: boolean;
}

interface MeetingFormProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: MeetingFormData) => void;
	formValues: MeetingFormSchema;
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void;
	validationErrors: FormErrors;
	submitLabel: string;
	isPending: boolean;
	title: string;
	showCarryOver?: boolean;
	showCalendarIntegration?: boolean;
	workspaceId?: string;
	calendarUpdateNotification?: string;
}

const MeetingForm = ({
	isOpen,
	onClose,
	onSubmit,
	formValues,
	onFormChange,
	validationErrors,
	submitLabel,
	isPending,
	title,
	showCarryOver = false,
	showCalendarIntegration = true,
	workspaceId,
	calendarUpdateNotification,
}: MeetingFormProps) => {
	// Calendar integration feature flag and settings
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: companyCalendarIntegrationSettings } = useCompanyCalendarIntegrationSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});
	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
	});

	// Determine if calendar integration should be visible
	const isCalendarIntegrationEnabled =
		ffIsCalendarIntegrationEnabled &&
		companyCalendarIntegrationSettings?.enabled &&
		userCalendarStatus?.isConnected &&
		["google", "outlook"].includes(userCalendarStatus?.provider || "") &&
		userCalendarStatus?.selectedCalendarId;
	// Generate the start date time using the start time and start date
	const startDateTime = useMemo(
		() => parse(formValues.startTime, "HH:mm", formValues.startDate),
		// For some reason, changing the start time does not trigger a re-render of the component ¯\_(ツ)_/¯
		[formValues.startTime, formValues.startDate]
	);
	// Debounce the start date time to prevent excessive API calls
	const [debouncedStartDate] = useDebounceValue(startDateTime, 100);
	// Get the carry over items using the start date time
	const { data: latestMeetingCarryOverItems, isLoading: isLatestMeetingCarryOverItemsLoading } =
		useLatestMeetingCarryOverItems(workspaceId ?? "", debouncedStartDate, showCarryOver && !!workspaceId);
	const carryOverItemsCount = latestMeetingCarryOverItems?.count ?? 0; // Reset carryover item checkbox if count is 0
	if (carryOverItemsCount === 0 && formValues.carryOver) {
		onFormChange({ carryOver: false });
	}
	const carryOverItemsLabel = `Carryover ${carryOverItemsCount} unchecked item${carryOverItemsCount === 1 ? "" : "s"} from the previous meeting`;
	const carryOverItemsInputDisabled = isPending || isLatestMeetingCarryOverItemsLoading || carryOverItemsCount === 0;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Combine date and time into full datetime
		const startDateTime = parse(formValues.startTime, "HH:mm", formValues.startDate);
		const endDateTime = parse(formValues.endTime, "HH:mm", formValues.endDate);

		const data = {
			name: formValues.name.trim(),
			description: formValues.description?.trim() || "",
			startDateTime: startDateTime,
			endDateTime: endDateTime,
			carryOver: formValues.carryOver,
			addToCalendar: formValues.addToCalendar,
		};

		onSubmit(data);
	};

	// Generate all time options for both dropdowns
	const timeOptions = useMemo(() => generateTimeOptions(formValues.startDate), [formValues.startDate]);

	// Start time should always show all options
	const availableStartTimeOptions = timeOptions;

	// End time options should only be restricted when start and end dates are the same day
	const availableEndTimeOptions = useMemo(() => {
		const endTimeOptions = generateTimeOptions(formValues.endDate);

		if (isSameDay(formValues.startDate, formValues.endDate)) {
			return endTimeOptions.filter((option) => option.value > formValues.startTime);
		}
		return endTimeOptions;
	}, [formValues.startDate, formValues.endDate, formValues.startTime, timeOptions]);

	return (
		<ContentModal
			isOpen={isOpen}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
			}
			subTitle={
				calendarUpdateNotification && (
					<div className="mt-4 text-center">
						<Text size="paragraph-3" weight="regular">
							{calendarUpdateNotification}
						</Text>
					</div>
				)
			}
			handleClose={onClose}
			buttons={[
				<div key="cancelButton">
					<ButtonStandard size="large" onClick={onClose} disabled={isPending}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="submitButton">
					<ButtonStandard
						size="large"
						color="green"
						type="submit"
						form="meeting-form"
						disabled={isPending || isLatestMeetingCarryOverItemsLoading}
						data-testid="meeting-form-submit-button"
					>
						{submitLabel}
					</ButtonStandard>
				</div>,
			]}
		>
			<form id="meeting-form" data-testid="meeting-form" onSubmit={handleSubmit}>
				<Input
					placeholder="Meeting name here"
					value={formValues.name}
					onChange={(val) => onFormChange({ name: val })}
					disabled={isPending}
					clearable={true}
					label="Meeting Name"
					labelSize="paragraph-3"
					maxLength={280}
					errorMessage={
						validationErrors.name
							? validationErrors.name
							: formValues.name.length > 280
								? "Name cannot exceed 280 characters"
								: ""
					}
					animatedError
					data-testid="meeting-name-input"
				/>
				<Spacer height={2} />
				<div className="flex flex-row gap-4">
					<InputWrapper id="start-date-input" label="Start Date" error={validationErrors.startDate}>
						<DatePicker
							textInputClassName="w-[201px]"
							value={formValues.startDate}
							onChange={(val) => handleStartDateChange(val, formValues, onFormChange)}
							disabled={isPending}
							hasIcon={true}
							clearable={true}
						/>
					</InputWrapper>
					<InputWrapper id="end-date-input" label="End Date" error={validationErrors.endDate}>
						<DatePicker
							textInputClassName="w-[201px]"
							value={formValues.endDate}
							onChange={(val) => handleEndDateChange(val, formValues, onFormChange)}
							disabled={isPending}
							hasIcon={true}
							clearable={true}
						/>
					</InputWrapper>
				</div>
				<Spacer height={2} />
				<div className="grid grid-flow-col grid-cols-2 gap-4">
					<div className="col-span-1 w-[201px]">
						<InputWrapper id="start-time-input" label="Start Time" error={validationErrors.startTime}>
							<SingleSelectInput<string>
								options={availableStartTimeOptions}
								value={formValues.startTime}
								onChange={(option) =>
									handleStartTimeChange({ value: option ?? "" }, formValues, onFormChange)
								}
								placeholder="Select a start time"
								disabled={isPending}
								data-testid="start-time-select"
							/>
						</InputWrapper>
					</div>
					<div className="col-span-1 w-[201px]">
						<InputWrapper id="end-time-input" label="End Time" error={validationErrors.endTime}>
							<SingleSelectInput<string>
								options={availableEndTimeOptions}
								value={formValues.endTime}
								onChange={(option) => {
									onFormChange({ endTime: option ?? "" });
								}}
								placeholder="Select an end time"
								disabled={isPending}
								data-testid="end-time-select"
							/>
						</InputWrapper>
					</div>
				</div>
				<Spacer height={2} />
				<InputTextArea
					value={formValues.description || ""}
					onChange={(val) => onFormChange({ description: val })}
					placeholder="Your meeting description and link"
					minRows={6}
					maxRows={6}
					disabled={isPending}
					label="Description"
					labelSize="paragraph-3"
					errorMessage={
						validationErrors.description
							? validationErrors.description
							: (formValues.description?.length || 0) > config.meetingDescriptionMaxLength
								? `Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
								: ""
					}
					data-testid="meeting-description-input"
				/>
				{showCarryOver && (
					<>
						<Spacer height={2} />
						<Checkboxes
							options={[{ value: "carryOver", label: carryOverItemsLabel }]}
							value={formValues.carryOver ? [{ value: "carryOver", label: carryOverItemsLabel }] : []}
							disabled={carryOverItemsInputDisabled ? ["carryOver"] : []}
							onChange={() => onFormChange({ carryOver: !formValues.carryOver })}
						/>
					</>
				)}
				{showCalendarIntegration && isCalendarIntegrationEnabled && (
					<>
						<Spacer height={2} />
						<Checkboxes
							options={[{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]}
							value={
								formValues.addToCalendar
									? [{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]
									: []
							}
							disabled={isPending ? ["addToCalendar"] : []}
							onChange={() => onFormChange({ addToCalendar: !formValues.addToCalendar })}
						/>
					</>
				)}
			</form>
		</ContentModal>
	);
};

export default MeetingForm;
